import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, Mail, TrendingUp, Users, CheckSquare, FileText } from "lucide-react";

const CATEGORIE_LABELS = {
  interesse: "Intéressé", demande_informations: "Demande d'informations", demande_tarifs: "Demande de tarifs",
  demande_devis: "Demande de devis", a_rappeler: "À rappeler", a_recontacter_plus_tard: "À recontacter plus tard",
  pas_interesse: "Pas intéressé", desinscription: "Désinscription", reponse_non_comprise: "Réponse non comprise",
};

const Stat = ({ label, value, accent }) => (
  <div className="rounded-lg border p-4">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`mt-1 font-display text-2xl font-semibold ${accent || ""}`}>{value}</p>
  </div>
);

export default function WeeklyReport() {
  const { user } = useAuth();
  const [rep, setRep] = useState(null);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const canSend = ["admin", "responsable_commercial"].includes(user?.role);

  const load = () => {
    api.get("/reports/weekly").then(({ data }) => setRep(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/reports/weekly/history").then(({ data }) => setHistory(data)).catch(() => {});
  };
  useEffect(load, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/weekly/send");
      const testMode = data.destinataires?.some((d) => d.statut === "mode_test");
      toast.success(testMode
        ? `Rapport généré et journalisé en MODE TEST pour ${data.destinataires.length} destinataire(s) — l'envoi réel s'activera dès qu'une clé Brevo sera configurée`
        : `Rapport envoyé à ${data.destinataires.length} destinataire(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="weekly-report-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Rapport hebdomadaire</h1>
          <p className="text-sm text-muted-foreground">
            Résumé des 7 derniers jours. Envoyé automatiquement chaque lundi matin aux administrateurs et responsables commerciaux.
          </p>
        </div>
        {canSend && (
          <Button onClick={sendNow} disabled={sending} data-testid="send-weekly-report-button">
            <Send size={15} className="mr-2" /> {sending ? "Envoi en cours…" : "Envoyer le rapport maintenant"}
          </Button>
        )}
      </div>

      {!rep ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="weekly-report-stats">
            <Stat label="Nouveaux prospects" value={rep.nouveaux_prospects} />
            <Stat label="E-mails envoyés" value={rep.emails_envoyes} />
            <Stat label="Réponses reçues" value={rep.reponses_recues} />
            <Stat label="Nouveaux leads" value={rep.nouveaux_leads} accent="text-amber-600" />
            <Stat label="Leads à traiter" value={rep.leads_a_traiter} />
            <Stat label="Opportunités créées" value={rep.opportunites_creees} />
            <Stat label="Opportunités gagnées" value={rep.opportunites_gagnees} accent="text-emerald-600" />
            <Stat label="CA gagné" value={`${(rep.ca_gagne || 0).toLocaleString("fr-FR")} €`} accent="text-emerald-600" />
            <Stat label="CA potentiel ajouté" value={`${(rep.ca_potentiel_ajoute || 0).toLocaleString("fr-FR")} €`} />
            <Stat label="Tâches terminées" value={rep.taches_terminees} />
            <Stat label="Tâches en retard" value={rep.taches_en_retard} accent={rep.taches_en_retard > 0 ? "text-red-600" : ""} />
          </div>

          <div className="rounded-lg border p-4">
            <h2 className="flex items-center gap-2 font-display text-sm font-semibold"><Mail size={15} /> Réponses par catégorie</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.keys(rep.reponses_par_categorie || {}).length === 0 && <p className="text-sm text-muted-foreground">Aucune réponse cette semaine.</p>}
              {Object.entries(rep.reponses_par_categorie || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <Badge key={k} variant="secondary">{CATEGORIE_LABELS[k] || k} : {v}</Badge>
              ))}
            </div>
          </div>
        </>
      )}

      <div>
        <h2 className="flex items-center gap-2 font-display text-sm font-semibold"><FileText size={15} /> Historique des rapports envoyés</h2>
        <div className="mt-2 space-y-2" data-testid="weekly-report-history">
          {history.length === 0 && <p className="text-sm text-muted-foreground">Aucun rapport envoyé pour le moment.</p>}
          {history.map((h) => (
            <details key={h.id} className="rounded-md border p-3 text-sm">
              <summary className="flex cursor-pointer flex-wrap items-center gap-2">
                <span className="font-medium">Semaine {h.semaine}</span>
                <Badge variant={h.auto ? "default" : "secondary"}>{h.auto ? "Automatique (lundi)" : "Manuel"}</Badge>
                {h.destinataires?.some((d) => d.statut === "mode_test") && <Badge variant="outline" className="border-amber-300 text-amber-700">MODE TEST — non expédié</Badge>}
                <span className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleString("fr-FR")}</span>
              </summary>
              <div className="mt-2 border-t pt-2">
                <p className="text-xs text-muted-foreground">Destinataires : {(h.destinataires || []).map((d) => d.email).join(", ")}</p>
                <pre className="mt-2 whitespace-pre-wrap rounded bg-muted/40 p-2 text-xs">{h.texte}</pre>
              </div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}
